import { Row } from "@tanstack/react-table";
import React from "react";

import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { ONE, TEN, ZERO } from "@/constants/constants";
import { useDrive } from "@/context/DriveContext";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { IDrive } from "@/interfaces/project-register.interface";
import { fileImagePreviewer } from "@/lib/file-image.utils";
import { cn, formatFileSize, renderFullName } from "@/lib/utils";
import Icons from "@/utils/Icons";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

// No props needed as we'll use context
export const DriveTableView: React.FC = () => {
  // Get everything from context
  const {
    data,
    folderId,
    getActionOptions,
    setFolderId,
    previousDir,
    pagination,
    setLocalPagination,
    handleFileNavigation,
  } = useDrive();

  // Define table columns
  const columns: ExtendedColumnDef<IDrive>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: "select",
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
      cell: ({ row }): React.JSX.Element => {
        const name = row.original.name ?? "";
        const isFolder = row?.original.type === "dir";
        const isBackItem = row.original.empty && name === "...";

        // Special handling for back navigation item
        if (isBackItem) {
          return (
            <div
              className='flex items-center space-x-4 text-sm cursor-pointer'
              onClick={(): void => {
                handleFileNavigation({
                  url: "",
                  isEmpty: true,
                  folderId,
                  previousDir,
                });
                setLocalPagination({
                  limit: TEN,
                  offset: ZERO,
                  currentPage: ONE,
                });
              }}
            >
              <h3 className='line-clamp-1 text-gray-500 font-medium'>...</h3>
            </div>
          );
        }

        if (isFolder) {
          return (
            <div
              className={cn(
                "flex items-center space-x-4 text-sm cursor-pointer",
              )}
              onClick={(): void => {
                setFolderId(row.original.id);
                setLocalPagination({
                  limit: TEN,
                  offset: ZERO,
                  currentPage: ONE,
                });
              }}
            >
              <Icons name='folder' size={25} />
              <p className='line-clamp-1'>{name}</p>
            </div>
          );
        }

        return (
          <div className='flex items-center space-x-4 text-sm cursor-pointer'>
            {row.original.empty ? (
              <p className='line-clamp-1'></p>
            ) : (
              fileImagePreviewer({
                file: row.original.url,
                className: "w-7 h-7",
                config: {
                  onClick: () =>
                    handleFileNavigation({
                      url: row.original.url,
                      isEmpty: !!row.original.empty,
                      folderId,
                      previousDir,
                    }),
                },
              })
            )}
            <p
              className={cn(
                "line-clamp-1",
                folderId && "cursor-pointer",
                row.original.empty && "text-2xl text-black/50",
              )}
            >
              {row.original.name}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "uploaded By",
      header: "Uploaded By",
      cell: ({ row }): React.ReactNode => (
        <h2 className='text-gray-700 text-nowrap'>
          {renderFullName({
            userData: {
              ...row.original.uploader,
              middleName: row.original.uploader?.middleName ?? undefined,
            },
          }) || "-"}
        </h2>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }): React.ReactNode => (
        <h2 className='text-gray-700 text-nowrap'>
          {row.original.empty
            ? ""
            : dateFormat(row.original?.date ?? "", "dd MMM yyyy")}
        </h2>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "size",
      header: "Size",
      cell: ({ row }): React.JSX.Element => {
        const size = row.original.empty
          ? ""
          : formatFileSize(
              row.original.size || ZERO,
              row.original.type === "dir",
            );

        return <h2 className='text-gray-700 text-nowrap'>{size}</h2>;
      },
      enableSorting: false,
    },
    {
      id: "actions",
      cell: ({ row }): React.ReactNode =>
        !row.original.empty ? (
          <CustomDropdown data={getActionOptions(row)} />
        ) : null,
    },
  ];

  return (
    <CustomTable
      title='Drive Table'
      data={data}
      columns={columns}
      empty={{
        btnLabel: "Add New Document",
        subtitle: DRIVE_CONSTANTS.MESSAGES.ADD_NEW,
      }}
      filters={false}
      functions={{
        columnToggle: true,
        add: false,
        RightClickData: (
          row: GlobalRowDataTypes<IDrive>,
        ): ActionButtonDataTypes[] => getActionOptions(row as Row<IDrive>),
        isExport: false,
        pagination,
      }}
      hide={{
        functions: true,
        filters: true,
      }}
      hasWritePermission={true}
    />
  );
};

export default DriveTableView;
